import { observable, action, decorate, observe } from 'mobx';
import { Geometry } from 'wkx';
import { Buffer } from 'buffer';

import RELATIONS from './relations';

/**
 * Базовый класс всех моделей хранилища
 */
export default class Model {
	/**
	 * Идентификатор объекта, не является observable так как никогда не меняется
	 */
	id;

	/**
	 * Признак выполнения запросов на сервер
	 * @type {boolean}
	 */
	@observable isLoading = false;

	/**
	 * Признак того что объект был изменен но не сохранен на сервере
	 */
	@observable isDirty = false;

	/**
	 * Признак того что объект был удален
	 */
	@observable isDeleted = false;

	/**
	 * Сознает новый объект модели на основе данных JSON или пустой объект
	 */
	constructor (json = {}) {
		this.fromJSON (json);

		// считаем что объекты созданные без id являются
		// новыми и требуют сохранения
		this.isDirty = this.id === undefined;

		// если объект новый то заносим его в кэш новых объектов
		this.id === undefined && this.constructor.CACHE.NEW.push (this);

		// включаем прослушку изменений свойств объектов после того как
		// проинициализировали все свойства объекта
		observe (this, this.onChange);
	}

	/**
	 * Функция вызывается при любом изменении свойств объекта,
	 * если значение свойства не изменилось функция не изменяется:
	 * this.name = 'test'; // вызывается если предыдущее значение было не 'test'
	 * this.name = 'test'; // не вызывается
	 * this.name = 'test2'; // вызывается
	 */
	@action onChange = (change) => {
		if (this.constructor.PROPERTIES[change.name]) {
			this.isDirty = true;
		}
	};

	/**
	 * Возвращает хранилище модели
	 */
	get TRANSPORT () {
		return this.constructor.TRANSPORT;
	}

	/**
	 * Возвращает модель объекта
	 */
	get MODEL () {
		return this.constructor;
	}

	/**
	 * Возвращает хранилище
	 */
	get STORE () {
		return this.constructor.STORE;
	}

	/**
	 * Вызывается при получении данных с сервера
	 */
	@action fromJSON (json) {
		// В качестве key может быть
		//   <relationId> - ключ реляции, например: clientId, projectId
		//   <relation>   - сама реляция, в случае если запрос использует include, например: client, project
		//   <property>   - обычное свойство объекта, например: name, text
		for (let key in json) {
			let relationId = Object.values(this.constructor.RELATIONS).find(rel => rel.foreignKey === key);
			let relation = this.constructor.RELATIONS[key];
			let property = this.constructor.PROPERTIES [key];
			if (relationId) {
				// записываем id реляции
				this [key] = json[key];

				// если с сервера приходит null, то чистим кэш реляции
				if (json[key] === null && this[`_${relationId.name}`]) {
					this[`_${relationId.name}`].clearCache(null);
				}
			} else if (relation) {
				this[relation.name] = json[key];
			} else if (this [key] !== json[key]) {
				if (property && (
					property.type === 'Geography' ||
					property.type === 'Geometry'
				)) {
					this [key] = json [key] ? Geometry.parse(Buffer(json[key], 'hex')).toGeoJSON() : null;
				} else {
					this [key] = json[key];
				}
			}
		}

	}

	toJSON () {
		let json = {
			id: this.id
		};

		for (let key in this.MODEL.PROPERTIES) {
			let property = this.MODEL.PROPERTIES[key];
			if (property && (
				property.type === 'Geography' ||
				property.type === 'Geometry'
			)) {
				json [key] = this [key] ? Geometry.parseGeoJSON(this [key]).toEwkb().toString('hex') : null;
			} else {
				json [key] = this [key];
			}
		}

		return json;
	}
	
	@action uploadFile (property, file) {
		return this.STORE.uploadFile (this.MODEL, this.id, property, file, this.MODEL.INFO.container);
	}

	@action downloadFile (property) {
		return this.STORE.downloadFile (this.MODEL, this.id, property, this.MODEL.INFO.container);
	}
	
	@action deleteFile (property) {
		return this.STORE.deleteFile (this.MODEL, this.id, property, this.MODEL.INFO.container);
	}

	@action async reload (include) {
		this.isLoading = true;

		try {
			// вызываем прямой метод на сервере
			let json = await this.TRANSPORT.invoke ({
				model: this.constructor.INFO,
				method: Model.INFO.methods.findById,
				args: [this.id, include], id: this.id
			});
			this.fromJSON (json);
			this.isDirty = false;
		} catch (error) {
			this.error = error;
		} finally {
			this.isLoading = false;
		}
	}

	@action async save () {
		this.isLoading = true;

		try {
			if (this.id) {
				await this.patchAttributes (this.toJSON ());
			} else {
				const res = await this.MODEL.create (this.toJSON ());
				Object.assign (this, res);
				// TODO: неправильно! временно!
				this.MODEL.CACHE.ID[this.id] = this;
				console.log('this', this);
			}
			this.isDirty = false;
		}
		catch (error) {
			this.error = error;
			throw error;
		}
		finally {
			this.isLoading = false;
		}
	}

	@action async delete () {
		this.isLoading = true;

		try {
			const res = await this.MODEL.deleteById (this.id);
			this.isDeleted = true;
			return res;
		}
		catch (error) {
			this.error = error;
			throw error;
		}
		finally {
			this.isLoading = false;
		}
	}

	/**
	 * Инициализация новой модели, возвращает класс новой модели
	 */
	static INIT (store, transport, info) {
		// Используется eval для задания динамического названия класаа
		// Старая версия (без имени класса):
		//   model = class extends Model {...}
		// Сделано для красивого stacktrace в дебагере браузера
		// От это возможно избавиться если генерить все декларации классов
		// на сервере, но этот метод плох тем что с сервера будет приезжать
		// дополнительный трафик этих файлов, когда можно все классы сгенерить на клиенте
		// TODO: обсуждается, предложения вэлкам
		let model = info.name === 'Model' ? Model : eval (`(class ${info.name} extends Model {
			constructor (json) {
				super (json);
			}
		})`);

		model.INFO = info;
		model.STORE = store;
		model.TRANSPORT = transport;
		model.PROPERTIES = info.properties;
		model.RELATIONS = info.relations;
		model.METHODS = info.methods;
		model.LAST_SYNC = + new Date ();

		// Специализированный кэш для каждой модели
		model.CACHE = {
			// Кэш результатов READ запросов
			READ: {},
			// Кэш объектов модели полученных с сервера, обновляется автоматически
			ID: {},
			// Объекты которые были созданы но не сохранены
			NEW: [],
		};

		model.INIT_PROPERTIES ();
		model.INIT_RELATIONS ();
		model.INIT_METHODS ();

		return model;
	}

	/**
	 * Инициализация свойств модели, формируются все observable свойства модели
	 * с помощью [decorate]{@link https://mobx.js.org/refguide/api.html#-decorate}
	 */
	static INIT_PROPERTIES () {
		let info = this.INFO;
		let properties = info.properties || {};
		let relations = info.relations || {};
		let observables = {};

		properties && Object.keys (properties).forEach (propertyName => {
			let property = properties[propertyName];
			// пропускаем свойство id, оно не должно быть observable
			(
				propertyName !== 'id' &&
				property.type !== 'Geography' &&
				property.type !== 'Geometry'
			) && (observables [propertyName] = observable);
		});
		relations && Object.keys (relations).forEach (relationName => {
			let relation = relations [relationName];
			// для belongsTo создается отдельное observable свойство (пример: task.projectId)
			observables [relation.foreignKey] = observable;
		});

		observables && decorate (this, observables);
	}

	/**
	 * Инициализация методов модели, формируется на основе описания модели
	 * возвращаемого по адресу http://<server>/api
	 */
	static INIT_METHODS () {
		let info = this.INFO;
		let methods = info.methods || {};

		methods && Object.keys (methods).forEach (methodName => {
			let method = methods [methodName];
			method.name = methodName;

			if (method.isStatic) {
				this [methodName] = function () {
					return this.PROCESS_INVOKE (method, arguments);
				};
			} else {
				this.prototype [methodName] = function () {
					return this.MODEL.PROCESS_INVOKE (method, arguments, this);
				};
			}
		});
	}

	/**
	 * Инициализация связей моделей, на основе информации о модели формируются разные связи:
	 *   belongsTo - геттер и сеттер {@link http://loopback.io/doc/en/lb3/BelongsTo-relations.html}
	 *   hasMany - функции find (), count (), create (), ... {@link http://loopback.io/doc/en/lb3/HasMany-relations.html}
	 *   hasAndBelongsToMany - функции find (), count (), create (), add () ... {@link http://loopback.io/doc/en/lb3/HasAndBelongsToMany-relations.html}
	 */
	static INIT_RELATIONS () {
		let info = this.INFO;
		let relations = info.relations || [];

		relations && Object.keys (relations).forEach (relationName => {
			let relation = relations [relationName];
			relation.name = relationName;

			Object.defineProperty (this.prototype, relationName, {
				get: function () {
					let relationInstance = this [`_${relation.name}`] || (
						this [`_${relation.name}`] = new RELATIONS [relation.type] (this.MODEL, this.STORE [relation.model], relation, this)
					);
					return relation.type === 'belongsTo' ? relationInstance.get() : relationInstance.get;
				},
				set: function (value) {
					let relationInstance = this [`_${relation.name}`] || (
						this [`_${relation.name}`] = new RELATIONS [relation.type] (this.MODEL, this.STORE [relation.model], relation, this)
					);
					return relation.type === 'belongsTo' ? relationInstance.set (value) : relationInstance.set(value);
				}
			});
		});
	}

	/**
	 * Функция конвертирует полученные результаты с сервера в сущности моделей
	 * this: Model - Модель в которой определен метод
	 *
	 * @param model	 {Model}	Модель у которой был вызван метод, отличается от this в слуях когда метод определен в родительской
	 * 							моделе а метод вызывается у дочерней модели
	 * @param method {Object} 	Информация о вызываемом методе
	 * @param args 	 {Array}	Аргументы с которыми был вызван метод
	 * @param instance {Model}	Объект для которого был вызван метод
	 */
	static async PROCESS_INVOKE (method, args, instance) {
		// проверяем наличие данных в кеше
		// Fixme: если при запросе сделать include - то в args попадает сама запись, а не id
		let result = this.PROCESS_CACHE (method, args, instance, async (args) => {
			// если в кеше нет вызываем запрос на сервер
			let result = await this.TRANSPORT.invoke ({
				model: this.INFO, method,
				args,
				id: instance && instance.id
			});

			// преобразование результата в объекты модели и обновление кэша объектов
			result = this.PROCESS_RESULT (method, result);

			return result;
		});

		return result;
	}

	/**
	 * Проверяет наличие данных в кеше, если не надохит вызывает invoke () для получения
	 * данных с сервера
	 * Кэшируются только методы с типом доступа READ такие как: find, findById, count, ...
	 */
	static async PROCESS_CACHE (method, args, instance, invoke) {
		// let key;
		let result;

		// Проверка на передачу уже полученных данных через include
		// TODO: стоит переделать, как-то кривовато
		// if (Array.isArray(args[0]) && method.name.startsWith('__get__')) {
		// 	result = observable(this.PROCESS_RESULT(method, args[0]));
		// 	args = {};
		// }

		// Кэширование производиться только для методов с типом доступа READ: find, findById, ...
		// if (method.accessType === 'READ' && this.STORE._useCache) {
		// 	key = `${this.INFO.name}.${method.name}(${JSON.stringify(args)})`;
		// 	result = result || this.CACHE.READ [key];
		//
		// 	// Обрабатываем частный случай когда объект запрашивает по id, по возможности
		// 	// возвращаем из кэша
		// 	if (result === undefined && method.name === 'findById') {
		// 		result = this.CACHE.ID [args [0]];
		// 	}
		// } else {
		// 	// TODO: подумать что еще чистить в кэше
		// 	this.CACHE.READ = {};
		// }

		// Если в кэше не нашли производим вызов метода на сервере
		if (result === undefined) {
			result = await invoke (args);

			// if (result === Object(result)) {
			// 	const totalCount = result.totalCount;
			// 	result = result instanceof Object ? observable(result) : result;
			// 	result.totalCount = totalCount;
			//
			// 	// добавляем функцию reload для результата, для повторного обновления
			// 	result.reload = async function () {
			// 		// очистка куша этого запроса
			// 		// delete model.CACHE.READ [key];
			//
			// 		// так как кэш этого запроса очищен будет вызов с сервера
			// 		let newResult = await invoke(arguments.length ? arguments : args);
			// 		if (Array.isArray(this)) {
			// 			this.length = newResult.length;
			// 		}
			// 		Object.assign(this, newResult);
			// 	};
			// }
		}

		// Сохраняем результат в кэш только для методов READ
		// if (method.accessType === 'READ' && this.STORE._useCache) {
		// 	// записываем в кэш
		// 	//result = observable(result);
		// 	this.CACHE.READ [key] = result;
		// }

		return result;
	}

	/**
	 * Обрабатывает результаты запроса, конвертируя их из сырых данных полученных с сервера
	 * Также заполняет/обновляет кэш объектов модели CACHE.INSTANCES и CACHE.ID
	 */
	@action static PROCESS_RESULT (method, result) {
		let returns = method.returns;
		let totalCount = result.totalCount || result.length;

		for (let element of returns) {
			let isArray = Array.isArray (element.type);
			let returnModel = isArray ? (
				element.type [0] === 'Model' ? this : this.STORE [element.type [0]]
			) : (
				element.type === 'Model' ? this : this.STORE [element.type]
			);

			if (isArray && Array.isArray (result)) {
				result = observable(result.map (data => returnModel.PROCESS_INSTANCE_CACHE (data)));
				result.totalCount = totalCount;
			} else if (returnModel) {
				result = observable(returnModel.PROCESS_INSTANCE_CACHE (result));
			} else if (element.root !== true) { // случай если в результате лежит примитивный тип
				result = result [element.arg];
			}
		}

		return result;
	}

	/**
	 * Формирует объект модели на основе сырых данных полученных с сервера
	 * Если объект был в кеше он будет обновлен и вернется, в противном случа
	 * будет создан новый объект с полученными данными
	 */
	@action static PROCESS_INSTANCE_CACHE (data) {
		let model = this;

		if (data === null) return null;

		let instance = model.CACHE.ID [data.id];

		if (instance === undefined) {
			// Если в кэше нет созданем новый объект и записываем в кэш
			instance = new model (data);
		} else {
			// Если в кеше есть, просто обновляем данные полученные с сервера
			// предполагается что сервере отдает самые свежие данные.
			// При этом, render вызовется только если значения изменились,
			// это обеспечивает mobx
			instance.fromJSON (data);
		}

		// записываем в кэш обновленный или созданный объект
		model.CACHE.ID [instance.id] = instance;

		return instance;
	}

	/**
	 * Полностью очишает кеш модели, вызывается для каждой модели отдельно
	 * Не рекомендуется использовать, только в особых случаях когда действительно на клиенте
	 * надо очистить кэш
	 * Рекомендуется использовать метод reload
	 */
	@action static CLEAR_CACHE () {
		this.CACHE.READ = {};
		this.CACHE.ID = {};
		this.CACHE.NEW = {};
		this.CACHE.DIRTY = {};
	}

	/**
	 * Синхронизация локальных изменений объектов и изменений данных на сервере
	 */
	@action static async SYNC () {
		// Только для моделей имеющих метод sync (mixin timestamp)
		if (!this.sync) {
			return;
		}

		// включаем в синхронизацию все новые объекты
		let newInstances = this.CACHE.NEW;
		let changes = {
			create: newInstances.map (instance => instance.toJSON ()),
			update: []
		};

		// и все объекты которые были изменены
		for (let id in this.CACHE.ID) {
			this.CACHE.ID [id].isDirty && changes.update.push (this.CACHE.ID [id].toJSON ());
		}

		// Вызвать синхронизацию изменившихся объектов
		let result = await this.sync (changes);

		// Обновляем данные созданных объектов
		for (let i = 0; i < newInstances.length; i ++) {
			let instance = newInstances [i];
			instance.fromJSON (result.create [i]);
			this.CACHE.ID [id] = instance;
			instance.isDirty = false;
		}

		// Обновляем данные обновленных объектов
		for (let json of result.update) {
			let instance = this.PROCESS_INSTANCE_CACHE (json);
			instance.isDirty = false;
		}

		// Обновляем данные удаленных объектов
		for (let id of result.delete) {
			this.CACHE.ID [id].isDeleted = true;
		}

		// Очищаем кэш новых объектов
		this.CACHE.NEW.length = 0;
	}
}
